import {
  ABILITY_EN_LABELS,
  getAbilityScoresForDisplay
} from '../utils/abilityVectorDisplay';
import apiClient from '../utils/apiClient.js';

/**
 * Archived 2025 subject recommendation hook.
 * Functionality: No-op replacement for legacy ability-similarity elective suggestions.
 * Input: userAbilities number[] (6D expanded to 8D internally)
 * Output: []
 * Collections: none
 */
export const getSubjectRecommendations = async (userAbilities) => {
  return [];
};

/**
 * Archived 2025 programme recommendation hook.
 * Functionality: No-op replacement for legacy ability-vector programme suggestions.
 * Input: userAbilities number[]
 * Output: []
 * Collections: none
 */
export const getProgramRecommendations = async (userAbilities) => {
  return [];
};

/**
 * Handler → POST /api/chatbot/generate (and related profile context)
 * Functionality: Build Eddy recommendation copy from user ability/profile via LLM.
 * Input: userData object (preferred_language, ability_6d, etc.)
 * Output: { title, text } recommendation payload
 * Collections: none directly (LLM); may read user via token context on server
 */
export const generateRecommendations = async (userData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Get user's language preference
    const userLanguage = userData.preferred_language || 'en';

    const abilityLines = getAbilityScoresForDisplay(userData)
      .map(({ slug, value }) => ({ label: ABILITY_EN_LABELS[slug], value }));

    const formatAbilityLine = (label, value) => {
      switch (userLanguage) {
        case 'tc':
          return `我嘅${label}係${value}分（滿分5分）。`;
        case 'sc':
          return `我的${label}是${value}分（满分5分）。`;
        default:
          return `My ${label} is ${value} out of 5. `;
      }
    };

    const abilitySummary = abilityLines
      .map(({ label, value }) => formatAbilityLine(label, value))
      .join('');

    // Create language-specific recommendation prompt
    let recommendationPrompt = '';
    switch (userLanguage) {
      case 'tc':
        recommendationPrompt = `Hi 愛迪生！我係${userData.Nickname || `${userData.Last_Name} ${userData.First_Name}`}，喺${userData.School_Name}讀緊中${userData.Form}。` +
          abilitySummary +
          `我應該讀咩科？多謝！`;
        break;
      case 'sc':
        recommendationPrompt = `嗨！爱迪生！我是${userData.Nickname || `${userData.Last_Name} ${userData.First_Name}`}，在${userData.School_Name}读中${userData.Form}。` +
          abilitySummary +
          `我应该读什么科？谢谢！`;
        break;
      case 'en':
      default:
        recommendationPrompt = `Hi Eddy! I am ${userData.Nickname || `${userData.Last_Name} ${userData.First_Name}`} in ${userData.School_Name}, form ${userData.Form}. ` +
          abilitySummary +
          `What should I study for HKDSE? Thanks!`;
        break;
    }

    // Get the main recommendation text
    const textResponse = await apiClient.post('/api/chatbot/generate', {
      text: recommendationPrompt,
      language: userLanguage
    });

    if (!textResponse.ok) {
      throw new Error('Failed to get recommendation text');
    }

    const textData = await textResponse.json();
    if (!textData || !textData.content) {
      throw new Error('Invalid recommendation text data received');
    }

    const recommendationText = textData.content;

    // Get the title (short advice) with the recommendation text as context
    const titleResponse = await apiClient.post('/api/chatbot/generate', {
      text: `Based on this recommendation: "${recommendationText}"\n\nPlease give me just strictly one phrase -- within 5 words -- as a word of advice; no punctuations, and nothing more.`,
      language: userLanguage
    });

    if (!titleResponse.ok) {
      throw new Error('Failed to get recommendation title');
    }

    const titleData = await titleResponse.json();
    if (!titleData || !titleData.content) {
      throw new Error('Invalid recommendation title data received');
    }

    return {
      title: titleData.content,
      text: recommendationText
    };
  } catch (error) {
    console.error('Error generating recommendations:', error);
    throw error;
  }
}; 
